/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Actions,
  AttributeDefinition,
  AttributeDefinitionValue,
  Cat,
  Company,
  MultiSelectOption,
  Resource,
  ResourceMeta,
  Subjects,
  TaskAssignedResource,
  TaskResponse,
  TaskType,
  getTaskType,
} from '@dispatch/shared/types';
import { Section, SectionDivider, TabPanel } from '@dispatch/web/ui';
import { Box, Chip } from '@mui/material';
import { grey } from '@mui/material/colors';
import React, { useMemo } from 'react';
import {
  Control,
  FieldErrors,
  FieldValue,
  FieldValues,
  UseFormGetValues,
  UseFormSetValue,
} from 'react-hook-form';
import { LocationInformation } from './LocationInformation';
import { ResourceList } from './ResourceList';
import { Ability } from '@casl/ability';
import { AssignedResourceSection } from './AssignmentInformationSection/AssignedResourceSection';
import { TaskSection } from './AssignmentInformationSection/TaskSection';
import { ClaimAttributesSection } from './AssignmentInformationSection/ClaimAttributesSection';
import { TimingSection } from './AssignmentInformationSection/TimingSection';
import { isEmpty } from 'lodash';
import { RelatedTasks } from './AssignmentInformationSection';

export const AssignmentInformation = function ({
  currentTab,
  errors,
  control,
  inspectionOptions,
  resourceData,
  resources,
  taskData,
  setValue,
  getValues,
  perils,
  renderTaskLock,
  permissions,
  handleReassign,
  resourceMeta,
  rescheduleReasons,
  isDirty,
  company,
  attributes,
  assignedResource,
}: {
  currentTab: number;
  errors: FieldErrors;
  control: Control<FieldValues>;
  attributes?:
    | {
        definition: AttributeDefinitionValue | undefined;
        definitionParent: AttributeDefinition | undefined;
        id: string;
        cat: Cat;
        nonCat: Cat;
        useProficiency: boolean;
        minimumProficiency: number;
        isPrepopulated: boolean;
      }[]
    | undefined;
  inspectionOptions?: { title: string; value: string }[];
  resourceData?: TaskAssignedResource | null;
  resources?: any;
  company?: Company;
  taskData?: TaskResponse;
  setValue?: UseFormSetValue<FieldValue<any>>;
  getValues?: UseFormGetValues<FieldValues>;
  renderTaskLock?: () => React.ReactNode;
  perils: MultiSelectOption[];
  permissions: Ability<[Actions, Subjects]> | undefined;
  handleReassign: ({
    resourceId,
    reasonCode,
    forceReassign,
  }: {
    resourceId: string;
    reasonCode: string;
    forceReassign: boolean;
  }) => void;
  resourceMeta?: ResourceMeta;
  rescheduleReasons?: MultiSelectOption[];
  isDirty: boolean;
  assignedResource: Resource | undefined;
}) {
  const [assignedResourceData, setAssignedResourceData] =
    React.useState<TaskAssignedResource | null>();
  const [isAssigning, setIsAssigning] = React.useState<boolean>(false);
  const isRequiredResource = !isEmpty(taskData?.$msg?.requiredResource);

  React.useEffect(() => {
    setAssignedResourceData(resourceData);

    if (isAssigning) {
      setIsAssigning(false);
    }
  }, [isAssigning, resourceData]);

  const canEdit = useMemo(() => {
    if (
      taskData?.$msg.taskType === 'COMPLEX' ||
      taskData?.$msg.taskType === 'COMPLEX_CHILD'
    ) {
      return permissions?.can('upsert', 'complex-loss');
    }

    return permissions?.can?.('update', 'task');
  }, [taskData?.$msg.taskType, permissions]);

  const hasUpsertPermissions =
    taskData?.$metadata?.targetQueue === 'cancelled_q' ? false : canEdit;

  const showResourceList = useMemo(() => {
    return (
      !taskData?.$msg?.lockedAt &&
      (taskData?.$msg?.assignmentHandling?.appointment ||
        taskData?.$msg?.taskType === TaskType.CLAIM_OWNER) &&
      hasUpsertPermissions
    );
  }, [
    hasUpsertPermissions,
    taskData?.$msg?.assignmentHandling?.appointment,
    taskData?.$msg?.lockedAt,
    taskData?.$msg?.taskType,
  ]);

  const renderResourceList = React.useMemo(() => {
    return showResourceList ? (
      <ResourceList
        control={control}
        data={resources || []}
        getValues={getValues}
        handleReassign={handleReassign}
        resourceMeta={resourceMeta}
      />
    ) : null;
  }, [
    showResourceList,
    control,
    resources,
    getValues,
    handleReassign,
    resourceMeta,
  ]);

  const renderTimeSection = React.useMemo(() => {
    return (
      ![TaskType.COMPLEX, TaskType.CLAIM_OWNER].includes(
        getTaskType(taskData?.$msg?.taskType)
      ) && (
        <>
          <SectionDivider />
          <Section title="Timing">
            <TimingSection
              taskData={taskData}
              control={control}
              errors={errors}
              hasUpsertPermissions={hasUpsertPermissions}
              hasAppointment={!!taskData?.$msg?.assignmentHandling?.appointment}
              setValue={setValue}
              rescheduleReasons={rescheduleReasons}
              isDirty={isDirty}
            />
          </Section>
        </>
      )
    );
  }, [
    control,
    errors,
    hasUpsertPermissions,
    isDirty,
    rescheduleReasons,
    setValue,
    taskData,
  ]);

  return (
    <TabPanel value={currentTab} index={0} data-testid="general-info-tab">
      <Box sx={{ backgroundColor: grey['A100'], padding: '24px !important' }}>
        <Section
          title="Assigned Resource"
          rightTopControl={renderTaskLock}
          badge={
            isRequiredResource ? (
              <Chip
                label="Required"
                color="warning"
                size="small"
                data-testid="required_chip"
              />
            ) : undefined
          }
        >
          <AssignedResourceSection
            company={company}
            assignedResourceData={assignedResourceData}
            resourceData={resourceData}
            isAssigning={isAssigning}
            assignedResource={assignedResource}
          />
        </Section>
        <br />
        {renderResourceList}
      </Box>
      <Box sx={{ padding: '24px !important' }}>
        <Section title={`Task`}>
          <TaskSection
            control={control}
            errors={errors}
            hasUpsertPermissions={hasUpsertPermissions}
            inspectionOptions={inspectionOptions}
            perils={perils}
            taskData={taskData}
          />
        </Section>
        <SectionDivider />
        <Section title="Claim Attributes">
          <ClaimAttributesSection
            control={control}
            errors={errors}
            hasUpsertPermissions={hasUpsertPermissions}
            attributes={attributes}
            setValue={setValue}
          />
        </Section>
        <SectionDivider />
        <Section title="Location">
          <LocationInformation
            name="claim.locations"
            control={control}
            errors={errors}
            taskData={taskData}
          />
        </Section>
        {renderTimeSection}
        {taskData?.$relatedTasks && taskData.$relatedTasks.length > 0 && (
          <>
            <SectionDivider />
            <Section title="Related Tasks">
              <RelatedTasks taskData={taskData} />
            </Section>
          </>
        )}
      </Box>
    </TabPanel>
  );
};
